import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EvidenceMark, useSeoAdminView } from "@/pages/admin/SeoAeoGeoUx.jsx";

const LAYER_ORDER = ["seo", "aeo", "geo", "gso", "llmo"];

function goHref(href, setSection, navigate) {
  try {
    const url = new URL(href, window.location.origin);
    const tab = url.searchParams.get("tab");
    const section = url.searchParams.get("section");
    if (tab === "seo" && section) {
      setSection(section);
      return;
    }
  } catch {
    /* fall through */
  }
  navigate(href);
}

function LayerCard({ layer, view }) {
  if (!layer) return null;
  const name = view === "dev" ? layer.dev : layer.simple;
  return (
    <div className="aio-layer-card" data-testid={`aio-layer-${layer.id}`}>
      <div className="aio-layer-card__name">{name}</div>
      <div className="aio-layer-card__score">{layer.score ?? "-"}</div>
      <div className="aio-layer-card__mark">{layer.mark || ""}</div>
    </div>
  );
}

function SignalList({ signals, view }) {
  if (!signals?.length) return null;
  return (
    <ul className="aio-signal-list">
      {signals.map((item) => (
        <li key={item.id} className="aio-signal">
          <EvidenceMark
            compact={view !== "dev"}
            mark={item.mark}
            kind={item.id}
            note={{
              mark: item.mark,
              label: view === "dev" ? item.dev : item.simple,
              title: `${item.mark} ${view === "dev" ? item.id : item.simple} ${item.mark === "O" ? "충분" : item.mark === "△" ? "일부 부족" : "부족"}`,
              reason: item.reason,
              fix: item.fix,
            }}
          />
          {view === "dev" ? (
            <p className="aio-signal__dev">
              {item.id} · bucket={item.aio_bucket} · used_by={(item.used_by || []).join(",")} · score={item.score}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export default function AioDashboard({ onSection }) {
  const { view } = useSeoAdminView();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState("");

  useEffect(() => {
    api.get("/admin/aio/overview")
      .then((r) => setData(r.data))
      .catch(() => toast.error("AIO 진단 로드 실패"));
  }, []);

  if (!data) {
    return <p className="text-sm text-white/50">AIO 준비도를 계산하는 중입니다.</p>;
  }

  const go = (href) => goHref(href, onSection, navigate);

  return (
    <div className="aio-dash" data-testid="aio-dashboard">
      <p className="aio-disclaimer">{data.disclaimer}</p>

      <div className="aio-hero">
        <div>
          <div className="aio-hero__kicker">{view === "dev" ? "AIO Readiness" : "종합 준비도"}</div>
          <div className="aio-hero__score" data-testid="aio-total">{data.score}</div>
          <div className="aio-hero__sub">100점 만점 · 공개 근거 준비도</div>
        </div>
        <div className="aio-layers">
          {LAYER_ORDER.map((id) => (
            <LayerCard key={id} layer={data.layers?.[id]} view={view} />
          ))}
        </div>
      </div>

      {view === "dev" ? (
        <details className="aio-math" open>
          <summary>가중치 · 시그널 · 계산 근거</summary>
          <p className="text-xs text-white/50 mb-2">O=1.0 · △=0.5 · X=0. 각 시그널은 AIO Total에 한 버킷에서만 가산됩니다.</p>
          <table className="aio-table">
            <thead>
              <tr>
                <th>버킷</th>
                <th>가중</th>
                <th>점수</th>
                <th>기여</th>
                <th>시그널</th>
              </tr>
            </thead>
            <tbody>
              {(data.items?.[0]?.aio?.buckets || []).map((row) => (
                <tr key={row.bucket}>
                  <td>{row.dev}</td>
                  <td>{row.weight_pct}%</td>
                  <td>{row.score ?? "-"}</td>
                  <td>{row.contribution}</td>
                  <td>{(row.signal_ids || []).join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-white/40 mt-2">위 표는 첫 질문의 Total 분해입니다. 사이트 점수는 질문별 Total 평균입니다.</p>
        </details>
      ) : null}

      <section className="aio-section" data-testid="aio-top-actions">
        <h4>{view === "dev" ? "TOP ACTIONS" : "지금 고치면 효과가 큰 항목"}</h4>
        {(data.top_actions || []).length === 0 ? (
          <p className="text-sm text-white/50">우선 보완할 공백이 없습니다.</p>
        ) : (
          <ol className="aio-actions">
            {data.top_actions.map((item, idx) => (
              <li key={`${item.query_id}-${item.gap_type}-${idx}`}>
                <div className="aio-actions__q">{item.query}</div>
                <div className="aio-actions__gap">
                  → {view === "dev" ? item.gap_dev : item.gap_simple} ({item.mark})
                </div>
                <p>{item.reason}</p>
                <p>→ {item.fix}</p>
                <p>→ {view === "dev" ? item.edit_dev : item.edit_simple}에서 수정</p>
                <button type="button" className="admin-row__btn" onClick={() => go(item.href)}>
                  {view === "dev" ? item.edit_dev : item.edit_simple}로 이동
                </button>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="aio-section">
        <h4>{view === "dev" ? "Query readiness" : "질문별 준비도"}</h4>
        <div className="seo-ux-table-wrap">
          <table className="aio-table">
            <thead>
              <tr>
                <th>질문</th>
                {LAYER_ORDER.map((id) => (
                  <th key={id}>{view === "dev" ? id.toUpperCase() : (data.layers?.[id]?.simple || id)}</th>
                ))}
                <th>{view === "dev" ? "AIO Readiness" : "종합"}</th>
              </tr>
            </thead>
            <tbody>
              {(data.items || []).map((row) => (
                <tr key={row.id} className={openId === row.id ? "is-open" : ""}>
                  <td>
                    <button type="button" className="aio-qbtn" onClick={() => setOpenId(openId === row.id ? "" : row.id)}>
                      {row.query}
                    </button>
                  </td>
                  {LAYER_ORDER.map((id) => (
                    <td key={id}>{row.unique?.[id]?.mark || "-"}</td>
                  ))}
                  <td>{row.aio?.score ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {(data.items || []).filter((row) => row.id === openId).map((row) => (
        <section key={row.id} className="aio-detail" data-testid={`aio-detail-${row.id}`}>
          <h4>{row.query}</h4>
          {view === "dev" ? (
            <p className="text-xs text-white/40 mb-2">
              id={row.id} · Total {row.aio?.score} · counted {row.aio?.signal_ids_counted?.length} signals
            </p>
          ) : null}
          <div className="aio-detail__layers">
            {LAYER_ORDER.map((id) => (
              <div key={id}>
                <h5>{view === "dev" ? id.toUpperCase() : row.layers?.[id]?.simple}</h5>
                <SignalList
                  view={view}
                  signals={(row.signals || []).filter((s) => (s.used_by || []).includes(id))}
                />
              </div>
            ))}
          </div>
          <h5>{view === "dev" ? "Gaps" : "부족한 점"}</h5>
          <ul className="aio-gaps">
            {(row.gaps || []).map((gap) => (
              <li key={gap.type}>
                <strong>{view === "dev" ? gap.dev : gap.simple}</strong> {gap.mark}
                <p>{gap.reason}</p>
                <p>O가 되려면: {gap.fix}</p>
                <button type="button" className="admin-row__btn" onClick={() => go(gap.href || "/admin?tab=seo&section=queries")}>
                  수정 화면으로 이동
                </button>
              </li>
            ))}
          </ul>
          {view === "dev" && row.citation ? (
            <div className="aio-cite">
              <h5>Citation chain</h5>
              <p>mark={row.citation.mark} cause={row.citation.cause} claim={String(row.citation.claim_ok)} evidence={String(row.citation.evidence_ok)} url={String(row.citation.url_ok)} site_source={String(row.citation.site_source_present)}</p>
              <p>{row.citation.reason}</p>
            </div>
          ) : null}
          {view === "dev" && row.retrieve ? (
            <div className="aio-cite">
              <h5>Retrievability</h5>
              <p>mark={row.retrieve.mark} tokens={(row.retrieve.tokens || []).join(", ")} matched={(row.retrieve.matched || []).join(", ")}</p>
              <p>{row.retrieve.reason}</p>
            </div>
          ) : null}
        </section>
      ))}

      <section className="aio-section">
        <h4>{view === "dev" ? "AI crawler (read-only)" : "AI 수집 상태"}</h4>
        <p className="text-sm text-white/60">{data.robots?.note}</p>
        <p className="text-sm text-white/70">
          {data.robots?.star_policy === "allowed" ? "일반 수집은 허용되어 있습니다." : "현재 수집이 차단되어 있습니다."}
          {data.robots?.sitemap_enabled ? " 사이트맵이 켜져 있습니다." : " 사이트맵이 꺼져 있습니다."}
        </p>
        {view === "dev" ? (
          <ul className="text-xs text-white/45 mt-2">
            {(data.robots?.ai_agents || []).map((row) => (
              <li key={row.name}>{row.name}: {row.mentioned ? "명시 규칙" : "User-agent: * 상속"} ({row.effective})</li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}
